import isEmpty from "lodash.isempty";
import taxonomyApi from "../../apis/taxonomyApi"
import { NEWS_DEFAULT_DESCRIPTION, NEWS_DEFAULT_TITLE } from "../constants/messages";

/**
 * Method to extract and prepare the filters, titles, and required data to create the directory SEO section
 * from the SEO filtered news url
 * @param {Object} filterParams     URL params
 * @param {Object[]} categories     List of news categories
 */
export const getNewsSEOData = async (filterParams = {}, categories = []) => {
    let pageTitle, pageDescription;
    let isoCode;
    let categoryCode;
    let menaCountries = [];
    let location = '';
    let categoryType = '';

    // create a custom/temporary code for the categories to create the filtered urls
    const categories_seo = categories.map(cat => {
        const code = generateTempCode(cat.name);
        cat.code = code;
        return cat;
    })

    // check if the category is part of the url params then get the category filter
    if ('category' in filterParams) {
        categoryCode = filterParams.category;
        const category = categories_seo.filter(cat => cat.code === categoryCode);

        categoryType = category[0];
    }

    // Get MENAPT countries for SEO section
    const menaCountriesPromise = taxonomyApi.getSEOCountries().then(
        response => {
            menaCountries = response;
            
            // check if the iso_code is part of the url params then get the location filter
            if ('iso' in filterParams) {
                isoCode = filterParams.iso;
                
                location = menaCountries.find(loc => loc.iso_code === isoCode.toUpperCase());
            }
        }
    )

    let filterPromises = [menaCountriesPromise];

    return Promise.all(filterPromises).then(() => {

        // prepare the new page title and description
        pageTitle = preparePageTitle(location, categoryType);
        pageDescription = preparePageDescription(location, categoryType);

        return {
            location_seo: location,
            locations_seo: menaCountries,
            category_seo: categoryType,
            categories_seo: categories_seo,
            table_title: pageTitle,
            page_title: pageTitle,
            page_description: pageDescription,
            showSEOSection: true
        }
    });

}

/**
 * Method to prepare the news directory page title
 * @param {Object} location     The location object from the filtered URL
 * @param {Object} category     The category object from the filtered URL
 */
export const preparePageTitle = (location, category) => {
    let pageTitle;

    if (isEmpty(location) && isEmpty(category)) {
        return NEWS_DEFAULT_TITLE;
    }
    
    if (!isEmpty(category)) {
        pageTitle = `${location.name} ${category.name} News | MAGNiTT`
    } else {
        pageTitle = `${location.name} Startups Ecosystem News | MAGNiTT`
    }

    return pageTitle;
}

/**
 * Method to prepare the news directory page description
 * @param {Object} location     The location object from the filtered URL
 * @param {Object} category     The category object from the filtered URL
 */
export const preparePageDescription = (location, category) => {
    let pageDescription;
    
    if (isEmpty(location) && isEmpty(category)) {
        return NEWS_DEFAULT_DESCRIPTION;
    }

    if (!isEmpty(category)) {
        pageDescription = `MAGNiTT ${category.name} news in ${location.name} is an online selection of information on startup funding, product developments and industry trends. Cover stories on startup exits, venture capital, emerging tech including cryptocurrencies and blockchain.`
    } else {
        pageDescription = `MAGNiTT ${location.name} news is an online selection of information on startup funding, product developments and industry trends. Cover stories on startup exits, venture capital, emerging tech including cryptocurrencies and blockchain.`
    }

    return pageDescription;
}

/**
 * Method to generate a custom/temporary code from the entity name
 * @param {String} name     Entity name
 */
const generateTempCode = (name = '') => {
    const code = String(`${name}`)
        .replace(/&/g, ' and ') // Replace any '&' symbol with equivalent ' and '
        .replace(/[^a-zA-Z0-9\u0600-\u06FF]/g, '-') // Allow alphanumeric and arabic only
        .replace(/\.{2,}|-{2,}/g, '-') // Replace any 2 repeated occurrences of replacement
        .replace(/^\.|\.$|^-|-$/g, '') // Eliminate dot or hyphen in beginning and ending
        .toLowerCase();

    return code;
}

/**
 * method to create SEO URLs: /en-:iso/path/:category
 * e.g /en-ae/news/fintech
 * @param {String} path             Directory path e.g news
 * @param {String} isoCode          Location ISO code
 * @param {String} categoryCode     Category custom code
 * @returns  a filtered URL
 */
export const getSEOUrl = (path = '', isoCode = '', categoryCode = '') => {
    categoryCode = categoryCode.replace('/','-')
    categoryCode = categoryCode.replace('&','and')

    const url = `/en-${isoCode.toLowerCase()}/${path.toLowerCase()}/${categoryCode.toLowerCase()}`

    return url;
}